/*
 * Hides menu links and brand names that lead nowhere to buy from. A link to
 * a collection (or a brand) stays hidden only while none of its products can
 * be bought, and is shown again on the first load after something is back in
 * stock. The server always renders every link, so crawlers and visitors
 * without JavaScript see the full menu; if the stock data cannot be read
 * nothing is hidden.
 *
 * Data comes from /collections/all?view=ts-stock, 50 products a page, which
 * lists the vendors and collections that have at least one purchasable
 * product. It is fetched one page at a time when idle and kept for 20 minutes
 * in localStorage, so most page views apply it at once and make no requests.
 * A failed fetch is not retried for 5 minutes.
 */
(function () {
  'use strict';

  var KEY = 'ts-stock-v1';
  var FAIL_KEY = 'ts-stock-failed-v1';
  var FAIL_COOLDOWN = 5 * 60 * 1000;
  var TTL = 20 * 60 * 1000;
  var MIN_COLLECTIONS = 20;
  var MIN_VENDORS = 10;
  var HIDDEN_ATTR = 'data-ts-stock-hidden';

  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function write(data) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(data));
    } catch (e) { /* storage unavailable */ }
  }

  function handleOf(anchor) {
    var path = (anchor.getAttribute('href') || '').split('?')[0].split('#')[0];
    var match = path.match(/^(?:https?:\/\/[^/]+)?\/collections\/([^/]+)\/?$/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function setHidden(node, hide) {
    if (hide) {
      node.setAttribute(HIDDEN_ATTR, '');
      node.hidden = true;
    } else if (node.hasAttribute(HIDDEN_ATTR)) {
      node.removeAttribute(HIDDEN_ATTR);
      node.hidden = false;
    }
  }

  function visibleCount(nodes) {
    return Array.prototype.filter.call(nodes, function (n) { return !n.hasAttribute(HIDDEN_ATTR); }).length;
  }

  function applyMenu(sets) {
    var inStock = function (a) {
      var handle = handleOf(a);
      return handle === null || sets.collections[handle] === true;
    };

    var listItems = document.querySelectorAll(
      '[data-ts-nav] .ts-panel__cols li, [data-ts-nav] .ts-panel__flat li, .ts-drawer__panel .ts-drawer__links li'
    );
    Array.prototype.forEach.call(listItems, function (li) {
      var a = li.querySelector(':scope > a');
      if (!a || li.closest('.ts-panel--more')) return;
      setHidden(li, !inStock(a));
    });

    var columns = document.querySelectorAll('[data-ts-nav] .ts-panel__col:not(.ts-panel__col--more)');
    Array.prototype.forEach.call(columns, function (col) {
      if (col.closest('.ts-panel--more')) return;
      var heading = col.querySelector(':scope > .ts-panel__heading');
      var links = col.querySelectorAll(':scope > ul > li');
      var hide = false;
      if (heading && !inStock(heading)) {
        hide = links.length === 0 || visibleCount(links) === 0;
      }
      setHidden(col, hide);
    });

    var groups = document.querySelectorAll('.ts-drawer__group');
    Array.prototype.forEach.call(groups, function (group) {
      var heading = group.querySelector(':scope > .ts-drawer__group-title');
      var links = group.querySelectorAll('.ts-drawer__links > li');
      var hide = heading && !inStock(heading) && visibleCount(links) === 0;
      setHidden(group, !!hide);
    });

    var panels = document.querySelectorAll('[data-ts-nav] .ts-panel__cols');
    Array.prototype.forEach.call(panels, function (cols) {
      var shown = visibleCount(cols.querySelectorAll(':scope > .ts-panel__col'));
      if (shown > 0) cols.style.setProperty('--ts-cols', Math.min(shown, 5));
    });
  }

  function applyBrands(sets) {
    var tiles = document.querySelectorAll('.ts-brands__grid [data-ts-collection], .ts-brands__grid [data-ts-vendor]');
    Array.prototype.forEach.call(tiles, function (tile) {
      var collection = tile.getAttribute('data-ts-collection');
      var vendor = tile.getAttribute('data-ts-vendor');
      var ok = collection ? sets.collections[collection] === true : sets.vendors[vendor] === true;
      setHidden(tile, !ok);
    });

    var names = document.querySelectorAll('.ts-brands__names li[data-ts-vendor]');
    Array.prototype.forEach.call(names, function (li) {
      setHidden(li, sets.vendors[li.getAttribute('data-ts-vendor')] !== true);
    });

    var letters = document.querySelectorAll('.ts-brands__group');
    Array.prototype.forEach.call(letters, function (group) {
      var empty = visibleCount(group.querySelectorAll('.ts-brands__names li')) === 0;
      setHidden(group, empty);
      var jump = document.querySelector('.ts-brands__letters a[href="#' + group.id + '"]');
      if (jump && jump.parentElement) setHidden(jump.parentElement, empty);
    });
  }

  function toSets(data) {
    var sets = { collections: {}, vendors: {} };
    data.collections.forEach(function (h) { if (h) sets.collections[h] = true; });
    data.vendors.forEach(function (v) { if (v) sets.vendors[v] = true; });
    return sets;
  }

  function apply(data) {
    var sets = toSets(data);
    applyMenu(sets);
    applyBrands(sets);
    document.dispatchEvent(new CustomEvent('ts:stock'));
  }

  function fetchPage(n, retried) {
    return fetch('/collections/all?view=ts-stock&page=' + n, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('stock ' + res.status);
        return res.text();
      })
      .then(function (text) { return JSON.parse(text.trim()); })
      .catch(function (err) {
        if (retried) throw err;
        return new Promise(function (resolve) { window.setTimeout(resolve, 1200); })
          .then(function () { return fetchPage(n, true); });
      });
  }

  function fetchAll() {
    var merged = { collections: {}, vendors: {} };
    function take(page) {
      page.collections.forEach(function (h) { merged.collections[h] = true; });
      page.vendors.forEach(function (v) { merged.vendors[v] = true; });
    }
    function next(n, pages) {
      if (n > pages) return Promise.resolve();
      return fetchPage(n).then(function (page) {
        take(page);
        return next(n + 1, pages);
      });
    }
    return fetchPage(1).then(function (first) {
      take(first);
      return next(2, first.pages);
    }).then(function () {
      var data = {
        at: Date.now(),
        collections: Object.keys(merged.collections),
        vendors: Object.keys(merged.vendors)
      };
      if (data.collections.length < MIN_COLLECTIONS || data.vendors.length < MIN_VENDORS) {
        throw new Error('stock data incomplete');
      }
      return data;
    });
  }

  function start() {
    var cached = read();
    if (cached && cached.collections && cached.vendors) {
      apply(cached);
      if (Date.now() - cached.at < TTL) return;
    }
    if (!window.fetch || !window.Promise) return;
    try {
      var failedAt = Number(window.localStorage.getItem(FAIL_KEY) || 0);
      if (Date.now() - failedAt < FAIL_COOLDOWN) return;
    } catch (e) { /* storage unavailable */ }
    var run = function () {
      fetchAll().then(function (data) {
        write(data);
        apply(data);
      }).catch(function () {
        try { window.localStorage.setItem(FAIL_KEY, String(Date.now())); } catch (e) { /* ignore */ }
      });
    };
    if (window.requestIdleCallback) {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 800);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
